import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import Actions from "../Redux/action";
import {
    getAccessControlSupervisorListSelector,
    getAccessControlLoadingSelector,
    getAccessControlTotalCountSelector,
    getAccessControlSuggestionsSelector,
} from "../Redux/reducer/AccessControlReducer";
import { getHostelListSelectSelector } from "../Redux/reducer/HostelReducer";
import { getUserData } from "../Redux/reducer/LoginReducer";
import AppLoader from "../component/AppLoader";
import { PaginationComponent } from "../component/PaginationComponent";
import { DeclinePopup } from "../component/DeclinePopup";
import nodatafound from "../asset/img/datanotfound_black.png";

const PAGE_SIZE = 10;
const SUGGESTIONS_DEBOUNCE_MS = 300;
const SUGGESTIONS_PAGE_SIZE = 8;

export const AccessControl = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(getUserData);
    const supervisorList = useSelector(getAccessControlSupervisorListSelector);
    const loading = useSelector(getAccessControlLoadingSelector);
    const totalCount = useSelector(getAccessControlTotalCountSelector);
    const suggestions = useSelector(getAccessControlSuggestionsSelector);
    const hostelList = useSelector(getHostelListSelectSelector);

    const [searchText, setSearchText] = useState("");
    const [hostelFilterId, setHostelFilterId] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [addHostelForRow, setAddHostelForRow] = useState(null);
    const [selectedHostelId, setSelectedHostelId] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [removeConfirmTarget, setRemoveConfirmTarget] = useState(null);
    const searchContainerRef = useRef(null);

    const isAdminOrClientAdmin = user?.user_type === "0" || user?.user_type === "6";

    useEffect(() => {
        if (!isAdminOrClientAdmin) {
            navigate("/dashboard");
            return;
        }
    }, [isAdminOrClientAdmin, navigate]);

    const fetchList = useCallback(() => {
        const params = { page: 1, page_size: PAGE_SIZE };
        if (hostelFilterId) params.hostel_id = hostelFilterId;
        dispatch(Actions.accessControlSupervisorList(params));
    }, [dispatch, hostelFilterId]);

    const fetchListForPage = useCallback(
        (page) => {
            const params = { page, page_size: PAGE_SIZE };
            if (searchText.trim()) params.search = searchText.trim();
            if (hostelFilterId) params.hostel_id = hostelFilterId;
            dispatch(Actions.accessControlSupervisorList(params));
        },
        [dispatch, searchText, hostelFilterId]
    );

    useEffect(() => {
        if (!isAdminOrClientAdmin) return;
        fetchList();
    }, [isAdminOrClientAdmin, fetchList]);

    useEffect(() => {
        if (!isAdminOrClientAdmin) return;
        dispatch(Actions.hostelListSelect({}));
    }, [dispatch, isAdminOrClientAdmin]);

    useEffect(() => {
        if (!searchText.trim()) {
            dispatch(Actions.accessControlSuggestionsClear());
            setShowSuggestions(false);
            return;
        }
        const timer = setTimeout(() => {
            const suggestParams = {
                search: searchText.trim(),
                page: 1,
                page_size: SUGGESTIONS_PAGE_SIZE,
            };
            if (hostelFilterId) suggestParams.hostel_id = hostelFilterId;
            dispatch(Actions.accessControlSuggestions(suggestParams));
            setShowSuggestions(true);
        }, SUGGESTIONS_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchText, hostelFilterId, dispatch]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSearchChange = (e) => setSearchText(e.target.value);

    const handleSuggestionSelect = (item) => {
        setSearchText(item.name);
        setShowSuggestions(false);
        dispatch(Actions.accessControlSuggestionsClear());
        setPageNumber(1);
        const params = { page: 1, page_size: PAGE_SIZE, search: item.name };
        if (hostelFilterId) params.hostel_id = hostelFilterId;
        dispatch(Actions.accessControlSupervisorList(params));
    };

    const handleSearchSubmit = () => {
        setShowSuggestions(false);
        dispatch(Actions.accessControlSuggestionsClear());
        setPageNumber(1);
        const params = { page: 1, page_size: PAGE_SIZE };
        if (searchText.trim()) params.search = searchText.trim();
        if (hostelFilterId) params.hostel_id = hostelFilterId;
        dispatch(Actions.accessControlSupervisorList(params));
    };

    const handleClearSearch = () => {
        setSearchText("");
        setPageNumber(1);
        setShowSuggestions(false);
        dispatch(Actions.accessControlSuggestionsClear());
        const params = { page: 1, page_size: PAGE_SIZE };
        if (hostelFilterId) params.hostel_id = hostelFilterId;
        dispatch(Actions.accessControlSupervisorList(params));
    };

    const handleHostelFilterChange = (e) => {
        const value = e.target.value;
        setHostelFilterId(value);
        setPageNumber(1);
        const params = { page: 1, page_size: PAGE_SIZE };
        if (searchText.trim()) params.search = searchText.trim();
        if (value) params.hostel_id = value;
        dispatch(Actions.accessControlSupervisorList(params));
    };

    const onChangeHandle = (page) => {
        setPageNumber(page);
        fetchListForPage(page);
    };

    const handleAddHostel = (row) => {
        setAddHostelForRow(row);
        setSelectedHostelId("");
    };

    const handleConfirmAddHostel = () => {
        if (!addHostelForRow || !selectedHostelId) {
            toast.error("Please select a hostel.");
            return;
        }
        const callback = (res) => {
            if (res?.status_code === 0) {
                toast.success("Hostel access added.");
                setAddHostelForRow(null);
                setSelectedHostelId("");
                fetchListForPage(pageNumber);
            } else {
                const msg = res?.response?.data?.message ?? res?.message ?? "Failed to add access.";
                toast.error(msg);
            }
        };
        dispatch(
            Actions.accessControlAddHostel(
                {
                    hosteller_id: addHostelForRow.id,
                    hostel_id: parseInt(selectedHostelId, 10),
                },
                callback
            )
        );
    };

    const handleRemoveHostel = (row, hostel) => {
        if (hostel.is_primary) {
            toast.info("Primary hostel cannot be removed here. Edit the supervisor's primary hostel in onboarding.");
            return;
        }
        setRemoveConfirmTarget({ row, hostel });
    };

    const handleConfirmRemoveHostel = () => {
        if (!removeConfirmTarget) return;
        const { row, hostel } = removeConfirmTarget;
        setRemoveConfirmTarget(null);

        const callback = (res) => {
            if (res?.status_code === 0) {
                toast.success("Hostel access removed.");
                fetchListForPage(pageNumber);
            } else {
                const msg = res?.response?.data?.message ?? res?.message ?? "Failed to remove access.";
                toast.error(msg);
            }
        };
        dispatch(
            Actions.accessControlRemoveHostel(
                { hosteller_id: row.id, hostel_id: hostel.id },
                callback
            )
        );
    };

    const getAvailableHostelsForRow = (row) => {
        if (!Array.isArray(hostelList)) return [];
        const existingIds = (row.hostels || []).map((h) => h.id);
        return hostelList.filter((h) => h.id && !existingIds.includes(h.id));
    };

    if (!isAdminOrClientAdmin) return null;

    return (
        <div className="ecommerce-widget">
            <h5 className="card-header">
                <i className="fa fa-lock me-2" aria-hidden="true" />
                Access Control
            </h5>
            <p className="text-muted small mb-0 px-3 pt-1">
                Manage which hostels each supervisor can access. Add or remove hostel access below.
            </p>

            {/* Search & Filters */}
            <div className="card border-3 border-top border-top-primary mb-3">
                <div className="card-body py-3">
                    <div className="row align-items-end g-3">
                        <div className="col-lg-3 col-md-4">
                            <label className="form-label small text-muted mb-1">Filter by hostel</label>
                            <select
                                className="form-select form-select-sm"
                                value={hostelFilterId}
                                onChange={handleHostelFilterChange}
                            >
                                <option value="">All hostels</option>
                                {Array.isArray(hostelList) &&
                                    hostelList.map((h) => (
                                        <option key={h.id} value={h.id}>
                                            {h.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="col-lg-6 col-md-8" ref={searchContainerRef}>
                            <label className="form-label small text-muted mb-1">Search supervisors</label>
                            <div className="input-group position-relative">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="fa fa-search text-muted" aria-hidden="true" />
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 ps-0"
                                    placeholder="Name, member ID "
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSearchSubmit();
                                        if (e.key === "Escape") setShowSuggestions(false);
                                    }}
                                    onFocus={() => searchText.trim() && suggestions.length > 0 && setShowSuggestions(true)}
                                />
                                {searchText.trim() && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleClearSearch}
                                        title="Clear search"
                                    >
                                        <i className="fa fa-times" aria-hidden="true" />
                                    </button>
                                )}
                                <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                                    <i className="fa fa-search me-1" aria-hidden="true" />
                                    Search
                                </button>
                                {showSuggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
                                    <div className="access-control-suggestions-dropdown">
                                        <ul className="list-group list-group-flush">
                                            {suggestions.map((item) => (
                                                <li
                                                    key={item.id}
                                                    className="list-group-item list-group-item-action py-2 cursor-pointer"
                                                    onClick={() => handleSuggestionSelect(item)}
                                                >
                                                    <span className="fw-medium">{item.name}</span>
                                                    <span className="text-muted small ms-2">({item.member_id})</span>
                                                  
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading && <AppLoader />}

            {/* Table */}
            <div className="card border-3 border-top border-top-primary">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table onboardTable table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0 py-3 ps-4">
                                        <i className="fa fa-user me-2 text-muted" aria-hidden="true" />
                                        Supervisor
                                    </th>
                                    <th className="border-0 py-3">Member ID</th>
                                    {/* <th className="border-0 py-3">Email</th> */}
                                    <th className="border-0 py-3">
                                        <i className="fa fa-building me-2 text-muted" aria-hidden="true" />
                                        Hostels
                                    </th>
                                    <th className="border-0 py-3 pe-4 text-end" style={{ minWidth: "180px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(supervisorList) && supervisorList.length > 0 ? (
                                    supervisorList.map((row) => (
                                        <tr key={row.id}>
                                            <td className="ps-4 fw-medium">{row.name}</td>
                                            <td>
                                                <code className="bg-light px-2 py-1 rounded small">{row.member_id}</code>
                                            </td>
                                            {/* <td className="text-muted">{row.email || "—"}</td> */}
                                            <td>
                                                <div className="access-control-hostel-list">
                                                    {(row.hostels || []).length > 0 ? (
                                                        (row.hostels || []).map((h) => (
                                                            <div
                                                                key={h.id}
                                                                className={`access-control-chip ${h.is_primary ? "access-control-chip-primary" : "access-control-chip-extra"}`}
                                                            >
                                                                <i className="fa fa-building-o access-control-chip-icon" aria-hidden="true" />
                                                                <span className="access-control-chip-text">{h.name}</span>
                                                                {h.is_primary && (
                                                                    <span className="access-control-chip-badge" title="Primary hostel">
                                                                        <i className="fa fa-star" aria-hidden="true" />
                                                                    </span>
                                                                )}
                                                                {!h.is_primary && (
                                                                    <button
                                                                        type="button"
                                                                        className="access-control-chip-remove"
                                                                        onClick={() => handleRemoveHostel(row, h)}
                                                                        title="Remove access"
                                                                        aria-label="Remove access"
                                                                    >
                                                                        <i className="fa fa-times" aria-hidden="true" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="text-muted fst-italic small">No hostels assigned</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="pe-4 text-end">
                                                {addHostelForRow?.id === row.id ? (
                                                    <div className="access-control-add-panel">
                                                        <div className="access-control-add-panel-inner">
                                                            <label className="access-control-add-label">Add hostel access</label>
                                                            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                                                <select
                                                                    className="form-select form-select-sm access-control-add-select"
                                                                    value={selectedHostelId}
                                                                    onChange={(e) => setSelectedHostelId(e.target.value)}
                                                                >
                                                                    <option value="">Choose hostel...</option>
                                                                    {getAvailableHostelsForRow(row).map((h) => (
                                                                        <option key={h.id} value={h.id}>
                                                                            {h.name}
                                                                        </option>
                                                                    ))}
                                                                    {getAvailableHostelsForRow(row).length === 0 && (
                                                                        <option value="" disabled>No more hostels</option>
                                                                    )}
                                                                </select>
                                                                <div className="d-flex gap-1">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={handleConfirmAddHostel}
                                                                        disabled={!selectedHostelId}
                                                                    >
                                                                        <i className="fa fa-check me-1" aria-hidden="true" />
                                                                        Add
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        onClick={() => {
                                                                            setAddHostelForRow(null);
                                                                            setSelectedHostelId("");
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleAddHostel(row)}
                                                        disabled={getAvailableHostelsForRow(row).length === 0}
                                                        title={
                                                            getAvailableHostelsForRow(row).length === 0
                                                                ? "Supervisor already has access to all hostels"
                                                                : "Add hostel access"
                                                        }
                                                    >
                                                        <i className="fa fa-plus me-1" aria-hidden="true" />
                                                        Add hostel
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="text-center py-5 px-4">
                                            <div className="py-4">
                                                <img
                                                    src={nodatafound}
                                                    alt="No data"
                                                    className="mb-3"
                                                    style={{ maxWidth: "140px", opacity: 0.9 }}
                                                />
                                                <p className="text-muted mb-1 fw-medium">
                                                    {searchText.trim()
                                                        ? "No supervisors match your search"
                                                        : "No supervisors found"}
                                                </p>
                                                <p className="text-muted small mb-0">
                                                    {searchText.trim()
                                                        ? "Try a different search term or clear the search."
                                                        : "Supervisors are created when you onboard a person with role Supervisor in the Onboarded List."}
                                                </p>
                                                {searchText.trim() && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm mt-3"
                                                        onClick={handleClearSearch}
                                                    >
                                                        Clear search
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {totalCount > PAGE_SIZE && Array.isArray(supervisorList) && supervisorList.length > 0 && (
                    <div className="card-footer bg-white border-0 pt-0 pb-3">
                        <PaginationComponent
                            onChangeHandle={onChangeHandle}
                            pageCount={totalCount}
                            pageNumber={pageNumber}
                        />
                    </div>
                )}
            </div>

            <style>{`
                .access-control-hostel-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    align-items: center;
                }
                .access-control-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    height: 32px;
                    padding: 0 12px;
                    border-radius: 16px;
                    font-size: 0.875rem;
                    font-weight: 500;
                    white-space: nowrap;
                }
                .access-control-chip-primary {
                    background: #f8f9fa;
                    color: #212529;
                    border: 1px solid #adb5bd;
                }
                .access-control-chip-extra {
                    background: #e9ecef;
                    color: #495057;
                    border: 1px solid #dee2e6;
                }
                .access-control-chip-icon {
                    font-size: 0.8rem;
                    color: #6c757d;
                }
                .access-control-chip-text {
                    max-width: 140px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .access-control-chip-badge {
                    margin-left: 2px;
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                .access-control-chip-remove {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    padding: 0;
                    margin-left: 2px;
                    border: none;
                    border-radius: 50%;
                    background: rgba(0,0,0,0.1);
                    color: #495057;
                    cursor: pointer;
                    transition: background 0.15s, color 0.15s;
                }
                .access-control-chip-remove:hover {
                    background: #adb5bd;
                    color: #fff;
                }
                .access-control-add-panel {
                    display: inline-block;
                    text-align: left;
                }
                .access-control-add-panel-inner {
                    background: #f8f9fa;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    padding: 10px 14px;
                    min-width: 260px;
                }
                .access-control-add-label {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6c757d;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }
                .access-control-add-select {
                    min-width: 180px;
                }
                .access-control-suggestions-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1050;
                    margin-top: 2px;
                    background: #fff;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    max-height: 280px;
                    overflow-y: auto;
                }
                .access-control-suggestions-dropdown .list-group-item {
                    border-radius: 0;
                    cursor: pointer;
                }
                .access-control-suggestions-dropdown .list-group-item:hover {
                    background: #f8f9fa;
                }
            `}</style>

            {removeConfirmTarget && (
                <DeclinePopup
                    title={`Are you sure you want to remove ${removeConfirmTarget.row?.name || "this supervisor"} from ${removeConfirmTarget.hostel?.name || "this hostel"}? They will no longer have access to this hostel.`}
                    onClose={() => setRemoveConfirmTarget(null)}
                    onSuccess={handleConfirmRemoveHostel}
                />
            )}
        </div>
    );
};
